"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { NAV_ITEMS } from "@/lib/constants";
import { bookingUrlFor } from "@/lib/booking";
import { MobileNav } from "./MobileNav";

// Factual modality labels for the two flagship treatments, shown under
// their names in the dropdown's signature rail. Not marketing copy.
const SIGNATURE_MODALITY: Record<string, string> = {
  "/treatments/silk-protocol": "Multi-Modality",
  "/treatments/volnewmer-rf-skin-tightening": "Radiofrequency",
  "/treatments/ultraformer-mpt-hifu": "HIFU",
};

// Single-column panel width. Matches the clamp maths in the reposition
// effect below: min(360px, viewport minus a 2rem gutter).
const PANEL_WIDTH = "min(360px, calc(100vw - 2rem))";

const gold = "var(--color-gold)";

// A flat list row with the gold hairline hover marker. Shared by the
// "All Treatments" list and the "Your Concern" list so both read as one
// family.
function FlatLink({
  href,
  label,
  onNavigate,
}: {
  href: string;
  label: string;
  onNavigate: () => void;
}) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      className="group/item flex items-center gap-2.5 py-2 text-[13.5px] leading-snug text-ink transition-colors hover:text-gold-deep"
    >
      <span
        aria-hidden="true"
        className="h-px w-3 flex-shrink-0 bg-ink/20 transition-all duration-300 group-hover/item:w-4 group-hover/item:bg-gold"
      />
      <span>{label}</span>
    </Link>
  );
}

// Shared bottom rail: the Book Now pill only. The "view all" links were
// removed as redundant: clicking the primary nav item opens the same page.
function DropdownRail({
  onNavigate,
  bookingHref,
}: {
  onNavigate: () => void;
  bookingHref: string;
}) {
  return (
    <div className="px-6 py-4 border-t silk-hairline flex items-center justify-end gap-4">
      <a
        href={bookingHref}
        target="_blank"
        rel="noopener noreferrer"
        className="silk-pill silk-pill--primary !px-5 !py-2 text-[13px] whitespace-nowrap shrink-0"
        onClick={onNavigate}
      >
        Book Now
      </a>
    </div>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Which dropdown is open, keyed by nav label. Only one at a time.
  const [openLabel, setOpenLabel] = useState<string | null>(null);
  // Horizontal correction (px, positive or negative) applied to the open
  // panel, which is centred on its trigger. 0 while the centred panel fits
  // the frame; otherwise the minimum nudge that keeps both edges inside the
  // gutter. On narrow screens the clamp wins over perfect centring.
  const [panelShift, setPanelShift] = useState(0);
  // SI-05 header states. `scrolled` flips when the top-of-page sentinel
  // leaves the viewport. Whether the page opens with a full-bleed hero
  // (data-hero-overlay) is resolved entirely in CSS via body:has(), so
  // the header is solid unless it sits over such a hero at the top.
  const [scrolled, setScrolled] = useState(false);
  // One wrapper element per dropdown, for outside-click and clamp maths.
  const menuRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const sentinelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const pathname = usePathname();

  const setMenuRef = (label: string) => (el: HTMLDivElement | null) => {
    if (el) menuRefs.current.set(label, el);
    else menuRefs.current.delete(label);
  };

  // Scroll sentinel: an absolutely positioned strip at the document
  // top. When it leaves the viewport, the header condenses and (over a
  // hero) crossfades to the cream state. Pure CSS transitions off the
  // resulting classes.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  // Close on click outside the open menu, or on Escape.
  useEffect(() => {
    if (!openLabel) return;
    const onDocClick = (e: MouseEvent) => {
      const el = menuRefs.current.get(openLabel);
      if (el && !el.contains(e.target as Node)) setOpenLabel(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenLabel(null);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openLabel]);

  // Centre the open panel on its trigger, then keep it inside the frame.
  // The panel is centred (translateX(-50%)) under the trigger midpoint; if
  // that would push either edge past the gutter, apply the smallest nudge
  // that pulls it back. Recomputes on resize while open. Measures the
  // (unshifted) trigger wrapper, so the maths never depends on the shift it
  // is producing.
  useEffect(() => {
    const measure = () => {
      if (!openLabel) {
        setPanelShift(0);
        return;
      }
      const wrapper = menuRefs.current.get(openLabel);
      if (!wrapper) return;
      const navItem = NAV_ITEMS.find(
        (i) => i.label === openLabel && "menu" in i
      ) as { menu?: string } | undefined;
      const maxWidth = navItem?.menu === "treatments" ? 720 : 360;
      const gutter = 16;
      const panelWidth = Math.min(maxWidth, window.innerWidth - 32);
      const rect = wrapper.getBoundingClientRect();
      const naturalLeft = rect.left + rect.width / 2 - panelWidth / 2;
      const naturalRight = naturalLeft + panelWidth;
      let shift = 0;
      if (naturalRight > window.innerWidth - gutter) {
        shift = window.innerWidth - gutter - naturalRight;
      }
      if (naturalLeft + shift < gutter) {
        shift = gutter - naturalLeft;
      }
      setPanelShift(shift);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [openLabel]);

  // Cancel any pending close timer if the component unmounts mid-delay.
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  const openMenu = (label: string) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenLabel(label);
  };
  // Tiny delay on mouseLeave so users can move from trigger to menu
  // without it slamming shut.
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenLabel(null), 120);
  };

  const closeMenu = () => setOpenLabel(null);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  const headerClasses = [
    "silk-header fixed top-0 inset-x-0 z-40",
    scrolled ? "is-scrolled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
    {/* Top-of-page sentinel for the SI-05 crossfade. Positioned against
        the document, not the fixed header, so it scrolls away. */}
    <div
      ref={sentinelRef}
      aria-hidden="true"
      className="absolute top-0 left-0 right-0 h-10 pointer-events-none"
    />
    <header className={headerClasses}>
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-16">
        <div className="silk-header-row flex items-center justify-between">
          {/* Wordmark: the woven SCA monogram sits left of the name; the
              type colour crossfades with the header state. */}
          <Link
            href="/"
            className="flex flex-shrink-0 items-center gap-2.5 sm:gap-3 group"
          >
            <Image
              src="/images/logo/sca-monogram.png"
              // Decorative by design, not an oversight: the link already
              // carries "Silk Clinical Aesthetics" as text, so alt text here
              // would make screen readers announce the brand twice. aria-hidden
              // states that intent so audits stop reading it as a missing alt.
              alt=""
              aria-hidden="true"
              width={44}
              height={44}
              preload
              className="h-9 w-9 sm:h-11 sm:w-11 shrink-0 object-contain"
            />
            {/* Two-line wordmark lockup: "SILK CLINICAL" sets the width;
                "AESTHETICS" is spread letter-by-letter with justify-between so
                it fills that exact width edge to edge (no guessed tracking).
                The spread word is aria-hidden with an sr-only equivalent so
                the accessible name stays "Silk Clinical Aesthetics". */}
            <span className="flex flex-col items-stretch leading-none">
              <span
                className="font-display text-2xl tracking-tight uppercase whitespace-nowrap transition-colors duration-[600ms]"
                style={{ color: "var(--hdr-fg)", fontWeight: 450 }}
              >
                Silk Clinical
              </span>
              <span
                aria-hidden="true"
                className="flex justify-between text-[9px] uppercase mt-0.5 transition-colors duration-[600ms]"
                style={{ color: "var(--hdr-fg-soft)" }}
              >
                {"AESTHETICS".split("").map((ch, i) => (
                  <span key={i}>{ch}</span>
                ))}
              </span>
              <span className="sr-only">Aesthetics</span>
            </span>
          </Link>

          {/* Desktop Navigation: distribute nav items evenly between logo
              and Book Now CTA. */}
          <nav
            aria-label="Main"
            className="hidden lg:flex flex-1 items-center justify-center gap-10 xl:gap-12 mx-10 xl:mx-14"
          >
            {NAV_ITEMS.map((item) => {
              if ("menu" in item) {
                const isOpen = openLabel === item.label;
                const menuId = `nav-menu-${item.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`;

                // The three flagship treatments lead the Treatments menu as a
                // signature rail; every other treatment stays grouped under its
                // category and flows into balanced columns (a mega panel, no
                // scroll). Concerns remain a narrow flat list.
                const isTreatments = item.menu === "treatments";
                let signature: { label: string; href: string }[] = [];
                let flat: { label: string; href: string }[] = [];
                let categories: {
                  category: string;
                  items: { label: string; href: string }[];
                }[] = [];
                if (isTreatments) {
                  signature = item.children.flatMap((c) =>
                    c.items.filter((i) => "featured" in i && i.featured)
                  );
                  categories = item.children
                    .map((c) => ({
                      category: c.category,
                      items: c.items.filter(
                        (i) => !("featured" in i && i.featured)
                      ),
                    }))
                    .filter((c) => c.items.length > 0);
                } else {
                  flat = [...item.concerns];
                }

                return (
                  <div
                    key={item.label}
                    ref={setMenuRef(item.label)}
                    className="relative"
                    onMouseEnter={() => openMenu(item.label)}
                    onMouseLeave={scheduleClose}
                    onFocus={() => openMenu(item.label)}
                    onBlur={(e) => {
                      // Only close if focus left the entire wrapper.
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        scheduleClose();
                      }
                    }}
                  >
                    <span className="inline-flex items-center">
                      <Link
                        href={item.href}
                        className="silk-nav-link py-2"
                        aria-current={isActive(item.href) ? "page" : undefined}
                      >
                        {item.label}
                      </Link>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={menuId}
                        aria-label={`${item.label} submenu`}
                        onClick={() =>
                          setOpenLabel((v) =>
                            v === item.label ? null : item.label
                          )
                        }
                        className="ml-1 p-1 transition-colors duration-[600ms]"
                        style={{ color: "var(--hdr-fg)" }}
                      >
                        <svg
                          className={`w-3 h-3 transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </span>

                    {/* Panel always in DOM for crawlers; visibility toggled
                        via the hidden attribute. Centred on the trigger
                        midpoint (left-1/2 + translateX(-50%)), then nudged by
                        panelShift if it would otherwise spill past a gutter. */}
                    <div
                      id={menuId}
                      hidden={!isOpen}
                      className="absolute top-full left-1/2 mt-0 pt-3"
                      style={{
                        transform: `translateX(calc(-50% + ${panelShift}px))`,
                      }}
                    >
                      <div
                        className="relative bg-paper border border-border/70 overflow-hidden rounded-2xl"
                        style={{
                          width: isTreatments
                            ? "min(720px, calc(100vw - 2rem))"
                            : PANEL_WIDTH,
                          boxShadow:
                            "0 20px 60px -20px rgba(26,26,26,0.18), 0 4px 12px -4px rgba(26,26,26,0.08)",
                        }}
                      >
                        {/* Gold accent rule across the top edge. */}
                        <div
                          aria-hidden="true"
                          className="absolute top-0 left-0 right-0 h-px z-10"
                          style={{
                            background: `linear-gradient(90deg, transparent 0%, ${gold} 50%, transparent 100%)`,
                          }}
                        />

                        {isTreatments ? (
                          // Mega panel: signature rail on the left, the rest
                          // grouped by category and flowed into two balanced
                          // columns so the whole menu fits without scrolling.
                          <div className="flex">
                            {signature.length > 0 && (
                              <div className="w-[232px] shrink-0 bg-oat/60 px-6 py-6 border-r silk-hairline">
                                <p className="silk-eyebrow mb-5">
                                  <span className="silk-eyebrow-num">·</span>
                                  <span className="silk-eyebrow-rule" />
                                  <span>Signature</span>
                                </p>
                                <ul className="space-y-5">
                                  {signature.map((sig, idx) => (
                                    <li key={sig.href}>
                                      <Link
                                        href={sig.href}
                                        onClick={closeMenu}
                                        className="group/sig block"
                                      >
                                        <span className="block font-display text-[11px] italic text-gold-bright">
                                          {`0${idx + 1}`}
                                        </span>
                                        <span className="mt-1 block font-display text-[16px] leading-[1.15] text-ink transition-colors group-hover/sig:text-gold-deep">
                                          {sig.label}
                                        </span>
                                        {SIGNATURE_MODALITY[sig.href] && (
                                          <span className="mt-1 block text-[10px] uppercase tracking-[0.2em] text-muted">
                                            {SIGNATURE_MODALITY[sig.href]}
                                          </span>
                                        )}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            <div className="flex-1 px-6 py-6">
                              <p className="silk-eyebrow mb-5">
                                <span className="silk-eyebrow-num">·</span>
                                <span className="silk-eyebrow-rule" />
                                <span>All Treatments</span>
                              </p>
                              <div className="columns-2 gap-x-8 [column-fill:balance]">
                                {categories.map((cat) => (
                                  <div
                                    key={cat.category}
                                    className="mb-5 break-inside-avoid"
                                  >
                                    <p className="mb-1 text-[10px] uppercase tracking-[0.18em] text-muted">
                                      {cat.category}
                                    </p>
                                    <ul>
                                      {cat.items.map((entry) => (
                                        <li key={entry.href}>
                                          <FlatLink
                                            href={entry.href}
                                            label={entry.label}
                                            onNavigate={closeMenu}
                                          />
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Concerns: a narrow single-column list (seven items,
                          // fits without scrolling).
                          <div className="px-6 py-6">
                            <p className="silk-eyebrow mb-4">
                              <span className="silk-eyebrow-num">·</span>
                              <span className="silk-eyebrow-rule" />
                              <span>By concern</span>
                            </p>
                            <ul>
                              {flat.map((entry) => (
                                <li key={entry.href}>
                                  <FlatLink
                                    href={entry.href}
                                    label={entry.label}
                                    onNavigate={closeMenu}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <DropdownRail onNavigate={closeMenu} bookingHref={bookingUrlFor(pathname)} />
                      </div>
                    </div>
                  </div>
                );
              }

              if ("external" in item && item.external) {
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="silk-nav-link"
                  >
                    {item.label}
                  </a>
                );
              }

              return (
                <Link
                  key={item.label}
                  href={item.href}
                  className="silk-nav-link"
                  aria-current={isActive(item.href) ? "page" : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Book Now CTA: outline pill that follows the header state
              (white over the hero, ink once solid). Wrapped in a plain div
              for the responsive toggle: `.silk-pill` sets an unlayered
              `display: inline-flex` that outranks Tailwind's layered
              `hidden`, so the hide has to live on a non-pill element. */}
          <div className="hidden lg:flex shrink-0">
            <a
              href={bookingUrlFor(pathname)}
              target="_blank"
              rel="noopener noreferrer"
              className="silk-pill silk-header-pill"
            >
              Book Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden p-3 -m-1 transition-colors duration-[600ms]"
            style={{ color: "var(--hdr-fg)" }}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </div>
      </div>
    </header>
    <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
